import { z } from 'zod';
import { BlockSchema, type Block } from './blocks.js';
import { EmailFooterConfigSchema, type EmailFooterConfig } from './settings.js';

// ─────────────────────────────────────────────────────────────────
// Email footer — authoring model
//
// The footer used to be a fixed set of fields the theme composed for
// everyone. It is now *content*: a list of ordinary blocks the user builds
// in the editor (a workspace default on site settings, optionally overridden
// per email), rendered by the theme through the same email block renderers
// as the body — so a footer can use any layout, colour and block the rest
// of the email can.
//
// Two things make that freedom safe:
//
//   1. System variables (below) let a footer line read live site data
//      ({{site_name}}, {{socials}}, …) instead of freezing today's values
//      into text. The legacy footer fields are kept as the *source* of those
//      variables, so an existing site keeps working and its footer can still
//      be driven by the same details panel.
//   2. An unsubscribe link is required in a custom footer — see
//      assertFooterUnsubscribe. Bulk email without one is not just rude, it
//      is illegal in most jurisdictions.
// ─────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────
// Per-email override
// ─────────────────────────────────────────────────────────────────
/**
 * A per-email footer override, stored (JSON) on the email row.
 *
 * `null` on the row means "inherit the workspace footer". An override with an
 * empty `blocks` array is meaningful and different: it renders no footer
 * content at all for that email.
 */
export const EmailFooterOverrideSchema = z
  .object({
    blocks: z.array(BlockSchema),
  })
  .strict();

export type EmailFooterOverride = z.infer<typeof EmailFooterOverrideSchema>;

// ─────────────────────────────────────────────────────────────────
// Reading stored footer data
// ─────────────────────────────────────────────────────────────────
/**
 * Read a stored footer config, however it happens to be stored.
 *
 * Two realities make a plain `schema.parse` wrong here:
 *
 *   - `site_settings.email_footer` is a jsonb column, but the settings route
 *     historically wrote a JSON *string* into it, so a row can hold either the
 *     object or the string form of one (and, in the worst case, both at once).
 *   - Footer rows predate this feature, so a field can be missing, stale or
 *     hand-edited. Dropping the whole footer because one URL is malformed would
 *     silently change what a site sends.
 *
 * So: unwrap strings, validate field by field, keep whatever parses and drop
 * only what does not. Unreadable data degrades to null, which means "the theme
 * renders its own default footer" — never a crash inside a send.
 */
export function parseEmailFooterConfig(value: unknown): EmailFooterConfig | null {
  let candidate: unknown = value;
  for (let depth = 0; depth < 2 && typeof candidate === 'string'; depth += 1) {
    try {
      candidate = JSON.parse(candidate);
    } catch {
      return null;
    }
  }
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return null;

  const input = candidate as Record<string, unknown>;
  const out: Record<string, unknown> = {};

  // Array-valued fields are salvaged item by item: one corrupt social link or
  // one unparseable block must not take the other twenty down with it.
  const arrayItemSchemas: Record<string, z.ZodTypeAny> = {
    blocks: BlockSchema,
    socials: EmailFooterConfigSchema.shape.socials.unwrap().element,
  };

  for (const [key, schema] of Object.entries(EmailFooterConfigSchema.shape)) {
    const raw = input[key];
    if (raw === undefined) continue;

    const itemSchema = arrayItemSchemas[key];
    if (itemSchema) {
      if (!Array.isArray(raw)) continue;
      const items = raw
        .map((item) => itemSchema.safeParse(item))
        .filter((result) => result.success)
        .map((result) => result.data);
      if (items.length) out[key] = items;
      continue;
    }

    const parsed = schema.safeParse(raw);
    if (parsed.success) out[key] = parsed.data;
  }
  const config = EmailFooterConfigSchema.safeParse(out);
  return config.success ? config.data : null;
}

/**
 * The footer an email actually renders: the workspace default, with a
 * per-email override replacing its *content* only.
 *
 * An override carries content, not identity — a one-off footer still reads the
 * site's logo, socials and address through the system variables, which is what
 * makes an override cheap to write and cheap to keep current.
 */
export function resolveEffectiveFooter(
  workspaceFooter: EmailFooterConfig | null | undefined,
  override: EmailFooterOverride | null | undefined,
): EmailFooterConfig | undefined {
  if (!override) return workspaceFooter ?? undefined;
  return { ...(workspaceFooter ?? { showUnsubscribe: true }), blocks: override.blocks };
}

// ─────────────────────────────────────────────────────────────────
// System variables
// ─────────────────────────────────────────────────────────────────
/** One system variable offered in the footer editor's variable picker. */
export interface FooterSystemVariable {
  /** Token name, used as `{{name}}`. */
  name: string;
  /** Human label for the picker. */
  label: string;
  /** What it resolves to, for the picker's help text. */
  description: string;
  /**
   * Whether the value is markup (so the editor hints that it belongs in its
   * own line/HTML block). Everything else is plain text.
   */
  html?: boolean;
}

/**
 * The system variables a footer may reference.
 *
 * These are resolved by the engine at build time and always win over an
 * email's own variables of the same name — `unsubscribe_url` in particular
 * must not be shadowable, or a workspace could accidentally send bulk mail
 * with a broken (or missing) opt-out.
 */
export const FOOTER_SYSTEM_VARIABLES: readonly FooterSystemVariable[] = [
  {
    name: 'site_name',
    label: 'Site name',
    description: 'The workspace site name, e.g. "Field Notes".',
  },
  {
    name: 'site_url',
    label: 'Site URL',
    description: 'The site base URL, e.g. https://example.com.',
  },
  {
    name: 'unsubscribe_url',
    label: 'Unsubscribe URL',
    description: 'Per-recipient unsubscribe link. A custom footer must include it.',
  },
  {
    name: 'year',
    label: 'Current year',
    description: 'Four-digit year at build time, e.g. 2026.',
  },
  {
    name: 'logo_url',
    label: 'Logo URL',
    description: 'Footer logo from the footer details panel.',
  },
  { name: 'logo_alt', label: 'Logo alt text', description: 'Alt text for the logo.' },
  { name: 'tagline', label: 'Tagline', description: 'One-line tagline from the footer details.' },
  {
    name: 'address',
    label: 'Mailing address',
    description: 'Postal address (sender identification).',
  },
  { name: 'legal_text', label: 'Legal text', description: 'Copyright/company line.' },
  {
    name: 'socials',
    label: 'Social links',
    description: 'The social links as inline HTML anchors — put it on its own line.',
    html: true,
  },
];

/** Names that are reserved: an email's own variables cannot shadow them. */
export const FOOTER_SYSTEM_VARIABLE_NAMES: readonly string[] = FOOTER_SYSTEM_VARIABLES.map(
  (variable) => variable.name,
);

/** Everything the system variables are resolved from. */
export interface FooterVariableContext {
  /** Workspace site name (`siteSettings.siteName`). */
  siteName?: string | undefined;
  /** Site base URL, no trailing slash. */
  siteUrl?: string | undefined;
  /** Per-recipient unsubscribe URL. */
  unsubscribeUrl?: string | undefined;
  /** Footer details: the field set, now the variables' data source. */
  footer?: Partial<EmailFooterConfig> | null | undefined;
  /** Build clock — injectable so builds and tests are deterministic. */
  now?: Date | undefined;
}

/** Escape a plain-text value for HTML text *or* attribute context. */
function escText(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Social links as a bullet-separated run of anchors (already-escaped HTML). */
function socialsHtml(socials: EmailFooterConfig['socials']): string {
  if (!socials?.length) return '';
  return socials
    .map((social) => `<a href="${escText(social.url)}">${escText(social.platform)}</a>`)
    .join('&nbsp;·&nbsp;');
}

/**
 * Resolve the system variables for one build.
 *
 * Values are HTML-escaped here rather than left to the templating step,
 * because blocks are resolved with escaping off (so `{{socials}}` can carry
 * markup). A text variable therefore arrives already escaped and is correct
 * in both text and attribute position.
 */
export function resolveFooterVariables(ctx: FooterVariableContext): Record<string, string> {
  const footer = ctx.footer ?? null;
  const now = ctx.now ?? new Date();
  return {
    site_name: escText(ctx.siteName ?? ''),
    site_url: escText(ctx.siteUrl ?? ''),
    unsubscribe_url: escText(ctx.unsubscribeUrl ?? ''),
    year: String(now.getFullYear()),
    logo_url: escText(footer?.logoUrl ?? ''),
    logo_alt: escText(footer?.logoAlt ?? ''),
    tagline: escText(footer?.tagline ?? ''),
    address: escText(footer?.address ?? ''),
    legal_text: escText(footer?.legalText ?? ''),
    socials: socialsHtml(footer?.socials),
  };
}

// ─────────────────────────────────────────────────────────────────
// Block string walking
// ─────────────────────────────────────────────────────────────────
/**
 * Rewrite every string in a block tree — `content`, every attr (including
 * nested blocks in section/columns/accordion), and every style value.
 *
 * Deliberately generic: a footer is free-form content, so a variable may sit
 * in a paragraph, a button href, an image `src`/`alt`, an HTML block or a
 * table cell. Walking the JSON means a new block type cannot be forgotten.
 */
export function mapBlockStrings(blocks: Block[], transform: (value: string) => string): Block[] {
  const walk = (value: unknown): unknown => {
    if (typeof value === 'string') return transform(value);
    if (Array.isArray(value)) return value.map(walk);
    if (value && typeof value === 'object') {
      const out: Record<string, unknown> = {};
      for (const [key, inner] of Object.entries(value)) out[key] = walk(inner);
      return out;
    }
    return value;
  };
  return walk(blocks) as Block[];
}

/** Every string in a block tree, for validation and inspection. */
export function collectBlockStrings(blocks: Block[]): string[] {
  const found: string[] = [];
  mapBlockStrings(blocks, (value) => {
    found.push(value);
    return value;
  });
  return found;
}

/**
 * Drop blocks that resolved to nothing.
 *
 * A footer is usually assembled from optional site details, so a line whose
 * variable was empty (`{{address}}` with no address configured) must not
 * leave a stranded paragraph behind — the same "render nothing when there is
 * nothing to render" rule the theme already applies to empty tables and
 * stats. Only text-carrying blocks are eligible: a divider, image, button or
 * table is a deliberate structural choice and always survives.
 */
export function dropEmptyFooterBlocks(blocks: Block[]): Block[] {
  const isBlank = (value: string): boolean =>
    value
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim().length === 0;

  return blocks.filter((block) => {
    if (block.type === 'paragraph' || block.type === 'heading') return !isBlank(block.content);
    if (block.type === 'html') return !isBlank(block.content);
    return true;
  });
}

// ─────────────────────────────────────────────────────────────────
// Unsubscribe requirement
// ─────────────────────────────────────────────────────────────────
/** Tokens that count as "the footer links to the unsubscribe page". */
const UNSUBSCRIBE_TOKENS = ['{{unsubscribe_url}}', '{{{unsubscribe_url}}}'];

/**
 * Whether footer blocks reference the unsubscribe link, either as the
 * `{{unsubscribe_url}}` token (before resolution) or as the resolved URL
 * (after it).
 *
 * Matched as a substring of any string in the tree, so it does not matter
 * whether the author used a link, a button, an HTML block or plain text.
 *
 * The resolved URL is matched in both spellings: `resolveFooterVariables`
 * HTML-escapes every value it hands over (a URL in an `href` must be escaped,
 * and the opt-out URL carries `&` between its query parameters), so by the
 * time the check runs the footer holds `…&amp;email=…` while the URL this
 * build was given has a bare `&`.
 */
export function footerReferencesUnsubscribe(
  blocks: Block[],
  unsubscribeUrl?: string | undefined,
): boolean {
  const needles = [
    ...UNSUBSCRIBE_TOKENS,
    ...(unsubscribeUrl ? [unsubscribeUrl, escText(unsubscribeUrl)] : []),
  ];
  return collectBlockStrings(blocks).some((value) =>
    needles.some((needle) => value.includes(needle)),
  );
}

/** Message used by the API and the build when a custom footer has no opt-out. */
export const FOOTER_UNSUBSCRIBE_MISSING_MESSAGE =
  'This email footer has no unsubscribe link. Add a link or button whose URL is ' +
  '{{unsubscribe_url}}, or use the system footer template. (Bulk email must carry an ' +
  'unsubscribe link; set the footer details option "unsubscribe link" off only if this ' +
  'workspace deliberately does not send bulk mail.)';

/**
 * Throws when a custom footer is missing its unsubscribe link.
 *
 * `showUnsubscribe: false` in the footer details is the deliberate, explicit
 * escape hatch — it already means "this site does not render an opt-out", so
 * honouring it here keeps one switch rather than two contradictory ones.
 */
export function assertFooterUnsubscribe(
  blocks: Block[],
  options: {
    unsubscribeUrl?: string | undefined;
    footer?: Partial<EmailFooterConfig> | null | undefined;
  },
): void {
  if (options.footer?.showUnsubscribe === false) return;
  if (footerReferencesUnsubscribe(blocks, options.unsubscribeUrl)) return;
  throw new Error(FOOTER_UNSUBSCRIBE_MISSING_MESSAGE);
}

// ─────────────────────────────────────────────────────────────────
// Starter / system footer template
// ─────────────────────────────────────────────────────────────────
/** Ids only need to be unique inside one document; `ulid` is a server dep. */
function defaultBlockId(): string {
  return `ftr${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

export interface StarterFooterInput {
  /** Footer details whose present fields each become a line. */
  footer?: Partial<EmailFooterConfig> | null | undefined;
  /** Id factory, injectable for deterministic tests. */
  newId?: (() => string) | undefined;
}

/**
 * The system footer template: the footer BlogForger ships, expressed as
 * ordinary editable blocks.
 *
 * Every data-driven line references a system variable instead of the value
 * itself, so the template stays live: changing the logo or the mailing
 * address in the footer details updates a footer built from it. Lines whose
 * detail is not configured are not generated, and an unsubscribe link is
 * always included unless the workspace turned opt-outs off.
 *
 * Used in two places, deliberately: the one-time migration of a site's
 * existing footer fields, and the locked "System footer" component in the
 * component library that users can insert into their footer and then edit.
 */
export function buildStarterFooterBlocks(input: StarterFooterInput = {}): Block[] {
  // Partial: every detail is optional, and an unset one means "no line for it".
  const footer: Partial<EmailFooterConfig> = input.footer ?? {};
  const newId = input.newId ?? defaultBlockId;
  const small = { fontSize: '13px', textAlign: 'center' as const };
  const textLine = (content: string): Block => ({
    id: newId(),
    type: 'paragraph',
    content: `<p>${content}</p>`,
    contentHasVar: content.includes('{{'),
    attrs: {},
    style: { ...small },
  });

  const blocks: Block[] = [
    { id: newId(), type: 'divider', content: '', attrs: {}, style: { borderColor: '#e0e0e0' } },
  ];

  if (footer.logoUrl) {
    blocks.push({
      id: newId(),
      type: 'image',
      content: '',
      attrs: {
        src: '{{logo_url}}',
        srcIsMustache: true,
        alt: footer.logoAlt ?? '{{logo_alt}}',
        fullWidth: false,
        fullHeight: false,
        width: 120,
        skipOptimize: false,
      },
      style: { ...small },
    });
  }

  if (footer.tagline) blocks.push(textLine('{{tagline}}'));
  if (footer.socials?.length) blocks.push(textLine('{{socials}}'));
  if (footer.address) blocks.push(textLine('{{address}}'));
  if (footer.legalText) blocks.push(textLine('{{legal_text}}'));

  if (footer.showUnsubscribe !== false) {
    // Same two lines the theme's default footer has always sent: who this is
    // from, and the way out. Both are editable like every other line.
    blocks.push(textLine('You are receiving this because you subscribed to {{site_name}}.'));
    blocks.push({
      id: newId(),
      type: 'html',
      content:
        '<p style="margin:0;font-size:13px;">' +
        '<a href="{{unsubscribe_url}}" style="color:inherit;text-decoration:underline;">Unsubscribe</a>' +
        '</p>',
      attrs: {},
      style: { ...small },
    });
  }

  return blocks;
}

/** Name of the locked component that carries the system footer template. */
export const SYSTEM_FOOTER_COMPONENT_NAME = 'System footer';
